import React, { useContext, useState } from "react";
import classNames from "classnames";
import { useUpdateSubscriptionReviewDecisionMutation } from "common/types/gqlSchemaTypes";
import { toStandardFormat } from "common/utils/dateUtils";
import { DropdownButton, Flex, Tag, Text, toast } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { SubscriptionStatus } from "apps/pillars/SubscriptionDocumentsPillar/constants";
import { type ReviewerType } from "../types";
import { SubscriptionReviewerDecision } from "../constants";
import { SubscriptionStaffReviewContextState } from "../context";
import { ApproveModal } from "./ApproveModal";
import { RejectModal } from "./RejectModal";
import styles from "./styles.scss";
import { refreshAndShowToast } from "../utils";

interface StaffReviewerStatusProps {
  reviewer: ReviewerType;
  isEditable?: boolean;
}

export function StaffReviewerStatus({
  reviewer,
  isEditable = false,
}: StaffReviewerStatusProps): React.ReactElement {
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateReviewStatus] = useUpdateSubscriptionReviewDecisionMutation();
  const { data, onStaffReviewCompleted } = useContext(SubscriptionStaffReviewContextState);
  const subscriptionStatus = data?.subscription?.status as SubscriptionStatus;

  if (subscriptionStatus === SubscriptionStatus.Canceled) {
    return <Text variant="secondary-md">Subscription canceled</Text>;
  }
  if (subscriptionStatus === SubscriptionStatus.Declined) {
    return <Text variant="secondary-md">Subscription declined</Text>;
  }

  if (reviewer.decision === SubscriptionReviewerDecision.Approve) {
    return (
      <Flex flexDirection="column">
        <Tag
          variant="success"
          className={classNames(styles.StaffReviewerStatus__statusTag, "stacked-xs")}
          data-cy="reviewer-approved-banner"
        >
          Approved
        </Tag>
        <Text variant="secondary-sm" align="right">
          {toStandardFormat(reviewer.modifiedAt)}
        </Text>
      </Flex>
    );
  }

  const handleSelectStatus = async (eventKey: string) => {
    if (eventKey === SubscriptionReviewerDecision.InProgress) {
      setIsDropdownLoading(true);
      await updateReviewStatus({
        variables: {
          objectInput: { reviewerId: reviewer.id, decision: eventKey },
        },
      });
      setIsDropdownLoading(false);
      toast.info("Marked in progress");
    }
    if (eventKey === SubscriptionReviewerDecision.Approve) {
      setShowApproveModal(true);
    }
    if (eventKey === SubscriptionReviewerDecision.Reject) {
      setShowRejectModal(true);
    }
  };

  const handleApprove = async (internalComments?: string) => {
    setIsSubmitting(true);
    const result = await updateReviewStatus({
      variables: {
        objectInput: {
          reviewerId: reviewer.id,
          decision: SubscriptionReviewerDecision.Approve,
          internalComments,
        },
      },
    });
    if (
      result.data?.updateSubscriptionReviewDecision?.__typename ===
      "UpdateSubscriptionReviewDecisionObject"
    ) {
      if (result.data.updateSubscriptionReviewDecision.isSubscriptionApproved) {
        onStaffReviewCompleted("Subscription staff review completed");
      } else {
        toast.info("Subscription approval submitted");
      }
    }
    setShowApproveModal(false);
    setIsSubmitting(false);
  };

  const handleReject = async (
    internalComments?: string,
    sendEmailToProspect?: boolean,
    emailComments?: string,
  ) => {
    setIsSubmitting(true);
    const result = await updateReviewStatus({
      variables: {
        objectInput: {
          reviewerId: reviewer.id,
          decision: SubscriptionReviewerDecision.Reject,
          internalComments,
          sendEmailToProspect,
          emailComments,
        },
      },
    });
    if (result.data?.updateSubscriptionReviewDecision?.__typename === "MutationErrorResponse") {
      toast.error(result.data.updateSubscriptionReviewDecision.message);
    } else if (
      result.data?.updateSubscriptionReviewDecision?.__typename ===
        "UpdateSubscriptionReviewDecisionObject" &&
      result.data?.updateSubscriptionReviewDecision?.newSubscriptionId
    ) {
      refreshAndShowToast(
        "Subscription sent back to prospect",
        result.data.updateSubscriptionReviewDecision.newSubscriptionId,
      );
    }
    setShowRejectModal(false);
    setIsSubmitting(false);
  };

  const handleCloseModal = () => {
    if (showApproveModal) {
      setShowApproveModal(false);
    }
    if (showRejectModal) {
      setShowRejectModal(false);
    }
    setIsSubmitting(false);
  };

  const isInProgress = reviewer.decision === SubscriptionReviewerDecision.InProgress;
  const statusLabel = isInProgress ? "In progress" : "Not started";
  if (isEditable) {
    return (
      <>
        <DropdownButton
          label={statusLabel}
          variant="primary"
          isLoading={isDropdownLoading}
          data-testid="status-dropdown"
          data-cy="review-dropdown"
        >
          {reviewer.decision === null && (
            <DropdownButton.Action
              icon="ellipsis-h"
              hasDivider
              key="in_progress"
              eventKey="in_progress"
              onSelect={handleSelectStatus}
              data-cy="review-mark-in-progress"
            >
              Mark in progress
            </DropdownButton.Action>
          )}
          <DropdownButton.Action
            icon="arrow-right"
            key="approve"
            eventKey="approve"
            onSelect={handleSelectStatus}
            data-cy="review-approve"
          >
            Approve
          </DropdownButton.Action>
          <DropdownButton.Action
            color={Colors.Danger600}
            icon="arrow-left"
            key="reject"
            eventKey="reject"
            onSelect={handleSelectStatus}
            data-cy="review-reject"
          >
            Send back to prospect
          </DropdownButton.Action>
        </DropdownButton>
        {showApproveModal && (
          <ApproveModal
            isSubmitting={isSubmitting}
            handleApprove={handleApprove}
            handleCloseModal={handleCloseModal}
          />
        )}
        {showRejectModal && (
          <RejectModal
            isSubmitting={isSubmitting}
            subscriptionId={data?.subscription?.id || ""}
            handleReject={handleReject}
            handleCloseModal={handleCloseModal}
          />
        )}
      </>
    );
  }

  const statusVariant = isInProgress ? "informative" : "warning";
  return (
    <Tag variant={statusVariant} className={styles.StaffReviewerStatus__statusTag}>
      {statusLabel}
    </Tag>
  );
}
